import api from 'api/api.config';
import { buildQueryParams } from 'helpers/api/build-query-params';
import type {
  SortDirection,
  FilterValue,
} from 'helpers/api/build-query-params';
import { parsePaginationHeaders } from 'helpers/api/parse-pagination-headers';

import type { CollectionResponse } from 'api/market-view-api';

const getModelFundsPath = (userId: string, modelId: string | number): string =>
  `/${userId}/aid-model/${modelId}/aid-special-funds`;

const getClientFundsPath = (
  userId: string,
  clientId: string | number
): string => `/${userId}/clients/${clientId}/aid-special-funds`;

const MODEL_FUNDS_MAX_SIZE = 10000;

export const getModelSpecialFunds = async (
  userId: string,
  modelId: string | number
): Promise<unknown> => {
  const response = await api.get(getModelFundsPath(userId, modelId), {
    params: { size: MODEL_FUNDS_MAX_SIZE },
  });

  return response.data;
};

export interface ClientSpecialFundsParams {
  pageSize?: number;
  pageIndex?: number;
  sortBy?: SortDirection[];
  filters?: Record<string, FilterValue> | null;
  modelId?: string | number;
}

export const getClientSpecialFunds = async (
  userId: string,
  clientId: string | number,
  params: ClientSpecialFundsParams = {}
): Promise<CollectionResponse> => {
  const { pageSize, pageIndex = 0, sortBy, filters, modelId } = params;

  const queryParams = buildQueryParams({
    pageSize,
    pageIndex,
    sortBy,
    filters,
  });

  if (modelId) {
    queryParams.append('modelId', String(modelId));
  }

  const response = await api.get(getClientFundsPath(userId, clientId), {
    params: queryParams,
  });

  return {
    data: response.data,
    pagination: parsePaginationHeaders(response.headers, {
      pageIndex,
      pageSize,
    }),
  };
};

export const createModelSpecialFund = async (
  userId: string,
  modelId: string | number,
  data: unknown
): Promise<unknown> => {
  const response = await api.post(
    getModelFundsPath(userId, modelId),
    JSON.stringify(data)
  );

  return response.data;
};

export const updateModelSpecialFund = async (
  userId: string,
  modelId: string | number,
  fundId: string | number,
  data: unknown
): Promise<unknown> => {
  const response = await api.put(
    `${getModelFundsPath(userId, modelId)}/${fundId}`,
    JSON.stringify(data)
  );

  return response.data;
};

export const deleteModelSpecialFund = async (
  userId: string,
  modelId: string | number,
  fundId: string | number
): Promise<void> => {
  await api.delete(`${getModelFundsPath(userId, modelId)}/${fundId}`);
};
